import {
  BASES_LIMITS,
  GAME_QUOTAS,
  ICON_RULES,
  MODE_PHRASE_LIMITS,
  MOGZO_PHRASES,
  MULTIPLAYER_LIMITS,
  TURN_LIMITS,
} from '@mogzo/shared';
import { CodeBlock, Notice } from '@mogzo/ui';
import { Code, DocLink, DocTable, Lead } from '../parts';

const MB = 1024 * 1024;

export function Sdk() {
  return (
    <>
      <h1>The SDK</h1>
      <Lead>
        Games talk to MOGZO through the SDK. Templates include it as <Code>mogzo-sdk.js</Code>,
        which sets <Code>window.mogzo</Code>.
      </Lead>

      <h2 id="init">Start</h2>
      <CodeBlock
        title="game.js"
        code={`const { player, onMogzo } = await mogzo.init();
// player: { id, name, isGuest }
// onMogzo: false when the game was opened outside mogzo.com

const me = await mogzo.player.me();   // the same, fresh
mogzo.post.context();                 // the game post it was opened from (null for now)`}
      />
      <ul>
        <li>
          Call <Code>init()</Code> once, before anything else. It never fails: if MOGZO doesn’t
          answer within 10 seconds, the game plays as a guest.
        </li>
        <li>
          A player’s <Code>id</Code> stays the same in your game, and is different in every other
          game. Games never see a player’s email or account.
        </li>
      </ul>

      <h2 id="calls">How calls work</h2>
      <ul>
        <li>Every call returns a promise.</li>
        <li>
          A refused call rejects with a <Code>code</Code> and a <Code>message</Code>, such as{' '}
          <Code>too_many_calls</Code>. Each service’s page lists its codes.
        </li>
        <li>
          When MOGZO is very busy, a call can take a few seconds longer: the page waits and tries
          again for your game.
        </li>
        <li>
          Some calls are answered on the page or batched to save calls; each service’s page says
          which. See <DocLink page="limits">Limits</DocLink> for the totals.
        </li>
      </ul>

      <h2 id="update">Keeping it up to date</h2>
      <p>
        New SDK features come with new CLI versions. After <Code>mogzo update</Code>, run this in
        each game’s folder to give it the newest SDK (<Code>mogzo dev</Code> and{' '}
        <Code>mogzo upload</Code> remind you when it’s behind):
      </p>
      <CodeBlock title="Terminal" plain code="mogzo sdk update" />

      <h2 id="outside">Outside mogzo.com</h2>
      <p>
        Opened anywhere else, the SDK still works: the player is a guest, and saves and game data
        last only until the page closes, so your game still runs.
      </p>
    </>
  );
}

export function CloudSaves() {
  return (
    <>
      <h1>Cloud saves</h1>
      <Lead>
        Keep each player’s progress on MOGZO, the same on every device they play on. A save has a
        name and holds any value JSON can.
      </Lead>

      <h2 id="save">Save and load</h2>
      <p>
        Call these once <Code>mogzo.init()</Code> has resolved.
      </p>
      <CodeBlock
        title="game.js"
        code={`await mogzo.save.set('progress', { level: 4, coins: 120 });
const progress = await mogzo.save.get('progress'); // null if there's none
await mogzo.save.delete('progress');               // true if there was one
const saves = await mogzo.save.list();             // [{ key, bytes, updatedAt }]`}
      />
      <DocTable
        head={['Call', 'What it does']}
        rows={[
          [
            <Code key="c">save.set(name, value)</Code>,
            'Saves a value under a name, replacing what was there.',
          ],
          [<Code key="c">save.get(name)</Code>, 'The value, or null when there’s none.'],
          [<Code key="c">save.delete(name)</Code>, 'true if there was a save to delete.'],
          [
            <Code key="c">save.list()</Code>,
            'Every save’s name, size and time, without the values.',
          ],
        ]}
      />

      <h2 id="sent">When saves reach MOGZO</h2>
      <p>
        Save whenever something happens: a level done, a purchase, the end of a run. MOGZO batches
        the rest, so you don’t have to.
      </p>
      <ul>
        <li>
          A save’s first <Code>set</Code> each session reaches MOGZO at once, so a full quota shows
          up straight away.
        </li>
        <li>
          After that, MOGZO sends the newest value at most once a minute, and at once when the game
          pauses or the page is hidden or closed.
        </li>
        <li>
          <Code>get</Code> answers with the newest value straight away, so your game never sees the
          delay.
        </li>
      </ul>
      <Notice tone="warning" title="If the browser crashes, up to a minute of saves can be lost">
        Don’t rely on a save landing that very second. For something that must not be lost, use{' '}
        <DocLink page="game-data">game data</DocLink>, which goes at once.
      </Notice>

      <h2 id="limits">Limits</h2>
      <DocTable
        head={['What', 'Limit']}
        rows={[
          ['Saves per player, in each game', String(GAME_QUOTAS.savesPerPlayer)],
          ['Size of one save, as JSON', `${GAME_QUOTAS.saveBytes / 1024} KB`],
          [
            'Names',
            <>
              Letters, digits, <Code>.</Code> <Code>:</Code> <Code>-</Code> <Code>_</Code>; up to 64
              characters
            </>,
          ],
          [
            'Calls to Game Services, per player',
            `${GAME_QUOTAS.callsPerMinute} a minute, all services together`,
          ],
        ]}
      />

      <h2 id="errors">When a call is refused</h2>
      <CodeBlock
        title="game.js"
        code={`try {
  await mogzo.save.set('progress', state);
} catch (error) {
  if (error.code === 'save_too_large') trimHistory(state);
}`}
      />
      <ul>
        <li>
          <Code>save_too_large</Code>: the value is over {GAME_QUOTAS.saveBytes / 1024} KB as JSON.
        </li>
        <li>
          <Code>too_many_saves</Code>: the player already has {GAME_QUOTAS.savesPerPlayer} saves in
          this game; delete one first.
        </li>
        <li>
          <Code>storage_full</Code>: the game has used its {GAME_QUOTAS.storageBytes / MB} MB of
          saves and game data.
        </li>
        <li>
          <Code>too_many_calls</Code>: more than {GAME_QUOTAS.callsPerMinute} calls in a minute;
          slow down.
        </li>
      </ul>

      <h2 id="testing">Testing saves</h2>
      <p>
        Saves in your test community are kept apart from your published game’s, so testers never
        touch real players’ progress. See and change them on your project’s <strong>Data</strong>{' '}
        tab. <Code>mogzo dev</Code> keeps the test player’s saves in your browser and has a Clear
        saves button.
      </p>
    </>
  );
}

export function GameData() {
  return (
    <>
      <h1>Game data</h1>
      <Lead>
        Store documents your game shares or keeps per player, in named collections: levels,
        inventories, things players make. Each document has an id and any value JSON can hold.
      </Lead>

      <h2 id="use">Read and write</h2>
      <CodeBlock
        title="game.js"
        code={`const levels = mogzo.data.collection('levels');          // shared by every player
await levels.set('lava-run', { name: 'Lava Run', world: 2, par: 45 });
const level = await levels.get('lava-run');               // { id, data, mine, updatedAt } or null
const world2 = await levels.query({ where: { world: 2 }, orderBy: '-par', limit: 10 });

const bag = mogzo.data.collection('inventory', { scope: 'player' }); // this player only
await bag.set('main', { items: ['sword'] });`}
      />

      <h2 id="scopes">Shared and private collections</h2>
      <ul>
        <li>
          <strong>Game collections</strong> (the default) are shared: every player reads them. A
          player can add a document; only the player who made it can change or delete it. Documents
          you make in the dashboard can’t be changed by players.
        </li>
        <li>
          <strong>Player collections</strong> (<Code>scope: 'player'</Code>) hold each player’s own
          documents, private to them.
        </li>
        <li>Game data goes to MOGZO at once, unlike saves, which are batched.</li>
      </ul>

      <h2 id="queries">Queries</h2>
      <p>
        Queries match top-level fields exactly (<Code>where</Code>, up to 5), sort by one field (
        <Code>orderBy</Code>, <Code>-</Code> for highest first), and return up to 100 at a time (
        <Code>limit</Code>, <Code>offset</Code>). In game collections, <Code>mine: true</Code> gives
        just this player’s documents.
      </p>

      <h2 id="limits">Limits</h2>
      <DocTable
        head={['What', 'Limit']}
        rows={[
          ['Size of one document, as JSON', `${GAME_QUOTAS.documentBytes / 1024} KB`],
          ['Documents per player (player collections)', String(GAME_QUOTAS.documentsPerPlayer)],
          [
            'Documents per game (game collections)',
            GAME_QUOTAS.documentsPerGame.toLocaleString('en'),
          ],
          [
            'Collection names',
            <>
              Lowercase letters, digits, <Code>-</Code> and <Code>_</Code>, starting with a letter
            </>,
          ],
          ['Saves and game data together, per game', `${GAME_QUOTAS.storageBytes / MB} MB`],
        ]}
      />
      <p>
        See and edit test data on your project’s <strong>Data</strong> tab; live data is read-only
        there.
      </p>
    </>
  );
}

export function Leaderboards() {
  return (
    <>
      <h1>Leaderboards</h1>
      <Lead>Best scores for each player, for ever or starting fresh each day, week or month.</Lead>

      <h2 id="make">Make a board</h2>
      <p>
        On your project’s <strong>Data</strong> tab, under Leaderboards: a name, an id your game
        uses, highest or lowest first, a number or a time, when it starts fresh, and the lowest and
        highest allowed scores. Or from the CLI: <Code>mogzo leaderboards create</Code> (see{' '}
        <DocLink page="ai-tools">AI coding tools</DocLink>).
      </p>

      <h2 id="scores">Send and show scores</h2>
      <CodeBlock
        title="game.js"
        code={`const { best, rank, improved } = await mogzo.leaderboards.submit('high-score', 12850);
const { board, entries } = await mogzo.leaderboards.top('high-score', { limit: 10 });
// entries: [{ rank, playerId, name, score, me }]
await mogzo.leaderboards.aroundMe('high-score', { limit: 5 });
await mogzo.leaderboards.top('high-score', { period: 'previous' }); // last week's`}
      />
      <ul>
        <li>
          MOGZO keeps each player’s best per period and refuses scores outside the board’s limits.
          For a time board, send milliseconds: 62345 shows as 1:02.345.
        </li>
        <li>
          Send a score at the end of a run, not every frame: up to {GAME_QUOTAS.scoresPerMinute} a
          minute per player per board. Scores go to MOGZO at once.
        </li>
        <li>Players see each other’s display names on boards.</li>
        <li>
          To show a board in MOGZO’s own panel, use <Code>mogzo.ui.showLeaderboard</Code> (see{' '}
          <DocLink page="panels">MOGZO’s panels</DocLink>).
        </li>
      </ul>

      <h2 id="reset">When boards reset</h2>
      <p>
        Daily boards reset at 00:00 UTC, weekly ones on Monday at 00:00 UTC, monthly ones on the
        first. Past periods stay readable with <Code>period: 'previous'</Code> and in the dashboard.
      </p>

      <h2 id="test-live">Test and live</h2>
      <p>
        Your test community gets new and changed boards right away; live players get them when you
        publish a version. Once a board has scores, its order, format and reset lock.{' '}
        <Code>mogzo dev</Code> uses your test boards when you’re signed in. Up to{' '}
        {GAME_QUOTAS.leaderboards} boards per game.
      </p>
    </>
  );
}

export function Multiplayer() {
  return (
    <>
      <h1>Multiplayer</h1>
      <Lead>
        Players find each other by quick match or an invite code, and MOGZO shows the panels for it.
      </Lead>

      <h2 id="modes">Make a mode</h2>
      <p>
        A mode is one way people play your game together, such as a two-player duel or a party of
        six. Make modes on your project’s <strong>Multiplayer</strong> tab, or from the CLI with{' '}
        <Code>mogzo modes create</Code> (see <DocLink page="ai-tools">AI coding tools</DocLink>).
        Each has:
      </p>
      <ul>
        <li>
          A kind. <strong>Turns</strong>: players take turns on a shared state, live or hours apart
          (2 to {MULTIPLAYER_LIMITS.turnsPlayers} players). <strong>Bases</strong>: players attack
          another player’s saved base while they’re away. <strong>Realtime</strong>: everyone plays
          at once in a room (up to {MULTIPLAYER_LIMITS.realtimePlayers} players).
        </li>
        <li>How many players, and two teams or none.</li>
        <li>
          How players meet: quick match, invite codes or both. Quick match pairs players of a
          similar skill and widens the search the longer they wait. Players never see their rating.
        </li>
        <li>The time for a turn, and a leaderboard that counts each player’s wins.</li>
      </ul>
      <Notice tone="warning" title="Realtime is in testing">
        Realtime modes work in your test community and in <Code>mogzo dev</Code>. Until MOGZO opens
        realtime to players, a version that uses one can’t be submitted; the Multiplayer tab marks
        those modes <strong>Test only</strong>. Turns and bases aren’t affected.
      </Notice>

      <h2 id="play">Start a match</h2>
      <CodeBlock
        title="game.js"
        code={`let match;
// Opened from a friend's match link: join it
if (mogzo.multiplayer.invite()) match = await mogzo.multiplayer.join();

// Quick match: MOGZO finds players near this player's skill
match = await mogzo.multiplayer.find('duel');
// A private match: MOGZO shows a code and a link to share
match = await mogzo.multiplayer.create('duel');
// A friend's code, typed into MOGZO's panel
match = await mogzo.multiplayer.join();

match.players; // [{ id, name, me, host }]
match.me; match.host;`}
      />
      <ul>
        <li>
          Each call answers once the match starts. MOGZO’s panels show meanwhile, in your Game UI
          look: finding a player, the invite code, waiting for players. The game is paused while
          they’re open.
        </li>
        <li>
          If the player gives up, the call rejects with <Code>cancelled</Code>. Guests are asked to
          sign in, and it rejects with <Code>sign_in_needed</Code>.
        </li>
        <li>
          A quick match starts when it’s full, or after {MULTIPLAYER_LIMITS.quickStartSeconds}{' '}
          seconds with enough players. A host starts an invite match when enough have joined. An
          invite code works for {MULTIPLAYER_LIMITS.inviteMinutes} minutes.
        </li>
      </ul>

      <h2 id="results">Results</h2>
      <CodeBlock
        title="game.js"
        code={`// One of: who won (null for a draw), the players in order, or scores (higher is better)
const next = await match.end({ winner: winnerId });
// match.end({ ranking: [firstId, secondId] })
// match.end({ scores: { [match.me.id]: score } })

if (next.action === 'rematch' || next.action === 'new') play(next.match);`}
      />
      <ul>
        <li>
          MOGZO shows the results panel, with Rematch, New match and Done. <Code>end</Code> answers
          with what the player chose; for a rematch or a new match it’s the next match, started.
        </li>
        <li>
          A game can send only its own player’s score. MOGZO waits for the other players’ scores, up
          to {MULTIPLAYER_LIMITS.resultSeconds} seconds after the first.
        </li>
        <li>
          Results move each player’s hidden rating in the mode, and add a win to the mode’s
          leaderboard. Results that differ from ones already sent are counted against cheating.
        </li>
        <li>
          <Code>match.leave()</Code> leaves a match; once it has started the player is placed last.
        </li>
      </ul>

      <h2 id="turns">Turns</h2>
      <p>
        In a turns mode, players take turns on a shared state your game defines: a board, a hand of
        cards, a pile of stars. MOGZO keeps the turn order (the order players joined), whose turn it
        is and the turn timer, and every move, so a player can come back to a match hours later.
        MOGZO never checks your game’s rules: check a move in the game before sending it.
      </p>
      <CodeBlock
        title="game.js"
        code={`if (match.isMyTurn) await match.move({ take: 2 }, { stars: 19 }); // the move, and the state after it
match.turn; // { player, endsAt }, or null once it's over

// The others' moves, live, and turns MOGZO moved on
match.onMove(({ kind, move, by, state }) => draw(state)); // kind: move, skipped, forfeited, resigned
match.onEnd(() => showOver());

// Coming back later: "your turn" on the splash, then the match itself
const open = await mogzo.matches.mine(); // [{ id, modeName, players, myTurn, turn }]
const again = await mogzo.matches.open(open[0].id);

// Opened from a MOGZO notification ("It’s your turn"): straight into that match
const opened = mogzo.matches.opened();
if (opened) await mogzo.matches.open(opened);`}
      />
      <ul>
        <li>
          MOGZO tells players when it’s their turn, on the bell at mogzo.com and, if they asked for
          it, by email while they’re away. Opening it opens your game with{' '}
          <code>mogzo.matches.opened()</code> set to that match. A turn of an hour or more gets a
          reminder when it’s nearly up.
        </li>
        <li>
          A turn lasts the mode’s time for a turn, 30 seconds to 7 days. When it runs out, MOGZO
          skips the turn or the player loses the match, as the mode says. With one player left, they
          win.
        </li>
        <li>
          The state can be up to {TURN_LIMITS.stateBytes / 1024} KB and a move up to{' '}
          {TURN_LIMITS.moveBytes / 1024} KB, as JSON; a match can have up to{' '}
          {TURN_LIMITS.events.toLocaleString('en')} moves.
        </li>
        <li>
          <Code>move</Code> is refused when it isn’t the player’s turn (<Code>not_your_turn</Code>)
          or another move came first (<Code>out_of_date</Code>): call <Code>match.refresh()</Code>{' '}
          for the newest state. <Code>{'{ keepTurn: true }'}</Code> lets the player move again.
        </li>
        <li>
          While the game listens with <Code>onMove</Code>, a move reaches the other players within a
          second. A waiting game asks about once every {TURN_LIMITS.waitSeconds} seconds, so it uses
          few of its calls.
        </li>
        <li>
          The data browser’s Matches page shows every match, with its moves and state, in test and
          live.
        </li>
      </ul>

      <h2 id="rooms">Realtime rooms</h2>
      <p>
        In a realtime mode, the players are in a room together and your game sends them small
        messages many times a second: positions, shots, a ball. MOGZO finds the players with the
        same panels as matches, then your game talks to the room on its own connection. MOGZO relays
        messages without reading them and never runs your game’s rules.
      </p>
      <CodeBlock
        title="game.js"
        code={`const room = await mogzo.rooms.join('tag'); // quick match into a room
// or mogzo.rooms.create('tag') for a code to share, mogzo.rooms.joinCode() to type one

room.send({ x: 10, y: 4 }); // to everyone else
room.send(new Int16Array([x, y]).buffer); // bytes, for the smallest messages
room.onMessage((data, from) => move(from.id, data));
room.onPlayer((player, change) => toast(player.name, change)); // joined, away, back, left

// One game decides what must be decided once: the host, in the room state
if (room.isHost) room.setState({ round: 1, it: room.me.id });
room.onState((state) => draw(state));
room.onHost(() => takeOver()); // the host left; the next one carries on

const next = await room.end({ ranking: [first, second] }); // MOGZO's results panel`}
      />
      <ul>
        <li>
          Rooms hold up to {MULTIPLAYER_LIMITS.realtimePlayers} players. Each mode sets messages a
          second per player ({MULTIPLAYER_LIMITS.messageRate.min} to{' '}
          {MULTIPLAYER_LIMITS.messageRate.max}), the biggest message (
          {MULTIPLAYER_LIMITS.messageBytes.min} to {MULTIPLAYER_LIMITS.messageBytes.max} bytes) and
          the longest a room stays open ({MULTIPLAYER_LIMITS.roomMinutes.min} to{' '}
          {MULTIPLAYER_LIMITS.roomMinutes.max} minutes). More messages, or bigger ones, are dropped,
          and the game is told once.
        </li>
        <li>
          When a player’s connection drops, MOGZO shows “Reconnecting…” and keeps their place for 30
          seconds; the others see them as away. Back in time, they get the room state and carry on.
        </li>
        <li>
          The room state (up to 16 KB) is what newcomers need: who’s ready, the map, the score. In a
          mode where only the host keeps the game going, only the host sets it. When the host
          leaves, whoever has been in longest takes over.
        </li>
        <li>With late joining on, quick match also fills open places in rooms already playing.</li>
        <li>
          A room closes when the results are in (<Code>end()</Code>), at its length, or a minute
          after everyone leaves. The data browser’s Rooms page shows each room, who came and went,
          and its state; messages aren’t kept.
        </li>
        <li>
          In <Code>mogzo dev</Code>, open the page in more tabs: each is a player in the same rooms.
        </li>
      </ul>

      <h2 id="bases">Bases</h2>
      <p>
        In a bases mode, each player saves a base, and other players attack it while they’re away.
        The base is any JSON your game defines: a town, a maze, a burrow. MOGZO picks a base whose
        owner is near the attacker’s skill, isn’t playing your game right now and isn’t shielded,
        and keeps it for that attacker while they play. Your game draws the base and plays the
        attack; MOGZO never runs its rules.
      </p>
      <CodeBlock
        title="game.js"
        code={`await mogzo.bases.save('raid', { gem: 12, traps: [2, 9, 10, 21] }); // the player's own base
const mine = await mogzo.bases.mine('raid'); // { base, savedAt, shieldUntil }, or null

// Someone to attack, or null when every base is shielded or its owner is playing
const target = await mogzo.bases.findOpponent('raid'); // { id, player, base, endsAt }
const done = await mogzo.bases.reportAttack(target.id, { won: true, loot: { coins: 40 } });
done.loot; // the loot as MOGZO kept it

// Attacks on this player, newest first; \`new\` ones the game hasn't shown before
const raids = await mogzo.bases.inbox('raid');`}
      />
      <ul>
        <li>
          A base can be up to {BASES_LIMITS.baseBytes / 1024} KB as JSON, one per player and mode.
        </li>
        <li>
          After an attack the base is shielded for the mode’s shield (none to 3 days), and its owner
          sees it on the bell at mogzo.com.
        </li>
        <li>
          The attacker has the mode’s time for an attack (30 seconds to 15 minutes) to report how it
          went; a later result is refused (<Code>attack_over</Code>). A player can start the mode’s
          attacks an hour, up to {BASES_LIMITS.attacksPerHour.max}; after that{' '}
          <Code>findOpponent</Code> is refused (<Code>too_many_attacks</Code>).
        </li>
        <li>
          Loot is whole numbers by kind, up to {BASES_LIMITS.lootKinds} kinds. More than the mode’s
          most of a kind is cut to it. MOGZO keeps no coins or resources: the attacker’s game adds
          the loot <Code>reportAttack</Code> answers with, and the defender’s game takes away the
          loot of <Code>new</Code> attacks in its inbox.
        </li>
        <li>
          Both players’ hidden ratings move with each attack, and a win can count on the mode’s
          leaderboard.
        </li>
        <li>
          The data browser’s Attacks page shows every attack, with the base as the attacker got it,
          and flags loot that was cut, attacks faster than a person could play and players who keep
          hitting the hourly limit.
        </li>
        <li>
          In <Code>mogzo dev</Code>, save a base in one tab and attack it from another.
        </li>
      </ul>

      <h2 id="test-live">Test and live</h2>
      <p>
        Your test community gets new and changed modes right away; live players get them when you
        publish a version. Reviewers play with each other in the review community. In{' '}
        <Code>mogzo dev</Code>, open the page in more browser tabs: each is its own test player, so
        they can find each other or join by code. Up to {GAME_QUOTAS.modes} modes per game.
      </p>
    </>
  );
}

export function Achievements() {
  return (
    <>
      <h1>Achievements</h1>
      <Lead>Goals players unlock, with MOGZO’s pop-up and a list of what they’ve earned.</Lead>

      <h2 id="make">Make achievements</h2>
      <p>
        On your project’s <strong>Data</strong> tab, under Achievements: a name, an id your game
        uses, how to earn it, points, an icon, and steps if it counts up (“tap 100 times”). Or from
        the CLI: <Code>mogzo achievements create</Code>.
      </p>

      <h2 id="unlock">Unlock and count up</h2>
      <CodeBlock
        title="game.js"
        code={`const { achievement, newlyUnlocked } = await mogzo.achievements.unlock('first-win');
if (newlyUnlocked) showToast(achievement.name);   // true only the moment it unlocks

await mogzo.achievements.progress('tap-100', taps);  // unlocks at 100
const all = await mogzo.achievements.list();
// [{ id, name, description, points, icon, steps, progress, unlocked, unlockedAt, hidden }]`}
      />
      <ul>
        <li>
          Each achievement unlocks once per player; unlocking again is harmless and costs no call,
          since the page already knows.
        </li>
        <li>
          Progress never goes down, so send the player’s total, not the change. Progress that
          unlocks reaches MOGZO at once; other progress goes at most once a minute, newest total
          only.
        </li>
        <li>MOGZO shows its pop-up by itself on every unlock; don’t add your own.</li>
        <li>A hidden achievement shows as “Hidden achievement” with a lock until it’s earned.</li>
      </ul>

      <h2 id="icons">Icons</h2>
      <p>
        Pick one of MOGZO’s icons and a colour, or upload your own square PNG, WebP or JPEG,{' '}
        {ICON_RULES.minSide} to {ICON_RULES.maxSide} pixels on each side, up to{' '}
        {ICON_RULES.maxBytes / 1024} KB. <Code>icon</Code> in the list is the picture’s address.
      </p>

      <h2 id="test-live">Test and live</h2>
      <p>
        Your test community gets new and changed achievements right away; live players get them when
        you publish a version. On the Data tab, Reset takes one back from a tester so you can try
        the unlock again. <Code>mogzo dev</Code> uses your test achievements when you’re signed in.
        Up to {GAME_QUOTAS.achievements} per game.
      </p>
    </>
  );
}

export function RemoteConfig() {
  return (
    <>
      <h1>Remote config</h1>
      <Lead>
        Settings you change in the dashboard without a new build: difficulty, an event switch,
        prices.
      </Lead>

      <h2 id="read">Read it</h2>
      <CodeBlock
        title="game.js"
        code={`const config = await mogzo.config.get();   // {} until you set some
spawnEvery = config.spawnEvery ?? 1;`}
      />
      <ul>
        <li>
          Games get new values the next time they start. Reading it again within 5 minutes gives the
          same values, without a call.
        </li>
        <li>Always have a default in your code, so the game works before any config is set.</li>
      </ul>

      <h2 id="set">Set it</h2>
      <p>
        On your project’s <strong>Data</strong> tab, under Remote config. Test and live config are
        separate: try a change on test, then copy it to live. Up to {GAME_QUOTAS.configBytes / 1024}{' '}
        KB.
      </p>
      <p>
        In <Code>mogzo dev</Code>, type it into the Remote config box under the game and press
        Restart.
      </p>
      <p>
        To change values for a set time by themselves, like double points every weekend, use a{' '}
        <DocLink page="live-events">live event</DocLink>.
      </p>
    </>
  );
}

export function LiveEvents() {
  return (
    <>
      <h1>Live events</h1>
      <Lead>
        Config values that switch on and off by themselves, like double points every weekend, and
        rules MOGZO runs for you: an achievement for the week’s top 10, a new daily seed.
      </Lead>

      <h2 id="events">Events</h2>
      <p>
        Make them on your project’s <strong>Schedule</strong> tab. An event is on once (between two
        times, up to 90 days apart), every day (the same hours) or every week (like Friday 18:00 to
        Monday 00:00). Times are UTC, and an event turns on and off within a minute of them. Up to{' '}
        {GAME_QUOTAS.events} events per game.
      </p>
      <ul>
        <li>
          While it’s on, it changes the <DocLink page="remote-config">config</DocLink> values it
          lists, and only those. When it ends they go back to your config’s own. If two events
          change the same value at once, the one that turned on last wins.
        </li>
        <li>
          It can give each run a leaderboard of its own. Its id is the event’s, and scores your game
          sends to the board you pick count there too while it’s on. Each run starts fresh; past
          runs stay to look back on.
        </li>
        <li>Turn an event off to skip its runs without deleting it.</li>
      </ul>

      <h2 id="read">In your game</h2>
      <CodeBlock
        title="game.js"
        code={`const config = await mogzo.config.get();      // has the values of the events that are on
const multiplier = config.scoreMultiplier ?? 1;   // a default for when none is

const events = await mogzo.events.active();
// [{ id, name, startedAt, endsAt, leaderboard, config }]

mogzo.events.onChange((events) => {
  showBanner(events[0]);                          // one turned on or off while playing
});`}
      />
      <ul>
        <li>
          <Code>config.get</Code> and <Code>events.active</Code> are read once and reused for 5
          minutes, or until an event turns on or off. <Code>onChange</Code> checks again just after
          each change, without a call every minute.
        </li>
        <li>
          Keep sending scores to the game’s own board. Scores sent straight to an event’s board are
          refused (<Code>event_leaderboard</Code>). Show it with{' '}
          <Code>mogzo.ui.showLeaderboard(event.leaderboard)</Code>.
        </li>
        <li>Outside mogzo.com there are no events: always have a default.</li>
      </ul>

      <h2 id="rules">Rules</h2>
      <p>Rules run by themselves, once each time:</p>
      <DocTable
        head={['When', 'Do']}
        rows={[
          [
            'A leaderboard starts fresh (daily, weekly or monthly, at 00:00 UTC)',
            'Give the top players of the period that ended an achievement',
          ],
          ['An event ends', 'Give the top players of that run’s own board an achievement'],
          [
            'At set times (every day, every Monday, the 1st of each month)',
            'Set a config value: a random number, today’s date or a value you choose',
          ],
        ]}
      />
      <ul>
        <li>
          Players tied at the last place get the achievement too. Players who have it already keep
          it; it’s never given twice. Each one hears it on the bell.
        </li>
        <li>
          The Schedule tab shows what each rule did last, and what it couldn’t do (an achievement
          live doesn’t have yet, say), in Recent runs. Runs are kept 30 days.
        </li>
        <li>Rules that post, like a weekly recap, come with game posts.</li>
      </ul>

      <h2 id="test-and-live">Test and live</h2>
      <p>
        The test and live schedules are separate, like remote config. Live changes need no new
        version: players get them at the times you set. Try them in test first:{' '}
        <strong>Turn on now</strong> turns an event on for your testers for an hour, and{' '}
        <strong>Run now</strong> runs a rule once. Then <strong>Copy to live</strong> shows each of
        test’s events and rules as new, changed or the same as live’s: tick the ones to copy.
        Anything you leave unticked stays on live as it is. A rule that needs an achievement or
        board live doesn’t have yet fails until a published version brings it.
      </p>
      <p>
        Tick rows on the Schedule tab to turn several on or off, copy them, or delete them. Deleting
        moves them to <strong>Trash</strong> for 30 days, so nothing is lost by mistake: an event
        goes with the rules that wait for it to end and its board’s scores, and{' '}
        <strong>Restore</strong> puts it back on test or live, where it was. While in Trash it
        doesn’t run, and its id stays taken. After 30 days, or with <strong>Delete forever</strong>,
        it’s gone for good.
      </p>
      <p>
        In <Code>mogzo dev</Code>, the page lists your test events: tick one to turn it on there.
      </p>

      <h2 id="cli">From the CLI</h2>
      <CodeBlock
        title="Terminal"
        plain
        code={`mogzo events create double-points --name "Double points weekend" \\
  --every week --on "fri 18:00" --off "mon 00:00" \\
  --set scoreMultiplier=2 --leaderboard weekly-best
mogzo events rules create --when end:double-points --give weekend-star --top 3
mogzo events rules create --when daily --at 00:00 --set dailySeed=random:1-99999
mogzo events list`}
      />
      <p>
        They change your test schedule, and show on the Schedule tab as made from the CLI. See{' '}
        <DocLink page="cli">CLI commands</DocLink> for every option.
      </p>
    </>
  );
}

export function Chat() {
  return (
    <>
      <h1>Chat in a match</h1>
      <Lead>
        Players in a match can send each other quick phrases, and typed messages if the mode allows
        it. MOGZO draws the chat and keeps it safe; your game only opens it and shows phrases.
      </Lead>

      <h2 id="mode">Set it on the mode</h2>
      <p>
        Each mode on your project’s <strong>Multiplayer</strong> tab has a <strong>Chat</strong>{' '}
        setting, or use <Code>mogzo modes edit</Code>. Like the rest of the mode, test gets it at
        once and live with your next version.
      </p>
      <ul>
        <li>
          <strong>Players can send:</strong> nothing (the default), quick phrases only, or phrases
          and typing.
        </li>
        <li>
          <strong>How it looks:</strong> a <strong>panel</strong> (beside the game on a computer, a
          sheet over it on a phone, in your Game UI look), or <strong>compact</strong>: a few lines
          of white text on a dark see-through strip in a corner of the game, with a slim bar to type
          in. Compact suits fast games; pick the corner that stays clear of your controls.
        </li>
        <li>
          <strong>Quick phrases:</strong> MOGZO’s ({MOGZO_PHRASES.join(', ')}), then up to{' '}
          {MODE_PHRASE_LIMITS.count} of your own, of {MODE_PHRASE_LIMITS.length} letters each.
          Reviewers see them.
        </li>
      </ul>
      <CodeBlock
        title="Terminal"
        plain
        code={`mogzo modes edit tag --chat typed --chat-look compact --chat-corner bottom-left --phrases "Catch me!,You’re it!"
mogzo modes edit duel --chat phrases`}
      />

      <h2 id="game">In the game</h2>
      <p>
        Chat belongs to the match the game is in: once <Code>multiplayer.find</Code>,{' '}
        <Code>rooms.join</Code> or another call starts or opens one, these work. MOGZO opens it from
        its pause menu on a phone too, and counts new messages on the pause button.
      </p>
      <CodeBlock
        title="game.js"
        code={`chatButton.addEventListener('click', () => mogzo.chat.open()); // in the mode's look
mogzo.chat.open({ look: 'compact' });  // or 'panel', for this moment
mogzo.chat.close();
await mogzo.chat.say('GG');            // one of the match's phrases
mogzo.chat.onPhrase(({ player, phrase, mine }) => {
  showBubble(player.id, phrase);       // over whoever sent it
});`}
      />
      <ul>
        <li>
          Your game never sees what players type: only phrases, with the player’s id in your game.
        </li>
        <li>
          Before the player is in a match whose mode has chat, <Code>open</Code> and{' '}
          <Code>say</Code> are refused with <Code>no_match_chat</Code>.
        </li>
        <li>
          In <Code>mogzo dev</Code>, open the page in more tabs: players in the same match chat with
          each other there.
        </li>
      </ul>

      <h2 id="safety">Keeping it safe</h2>
      <p>
        Chat is for players 18 and over. Every message goes through MOGZO’s text safety: slurs and
        threats aren’t sent and swearing is hidden. Players can report a message, block someone, and
        turn chat over the game off in the pause menu; staff act on reports. A match’s chat is kept
        7 days after it ends, and shows in the players’ Messages while the match is on and for a day
        after.
      </p>
    </>
  );
}
